import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Cloud } from 'lucide-react'
import { fetchApiVersion } from '@/lib/api'
import { useI18n } from '@/lib/i18n'

/** App footer: brand, tool links and meta. */
export function Footer() {
  const [apiVersion, setApiVersion] = useState<string | null>(null)
  const { t, path } = useI18n()

  useEffect(() => {
    let cancelled = false
    // Best-effort: hides silently when the API is unreachable.
    fetchApiVersion().then((data) => {
      if (!cancelled && data) setApiVersion(data.version)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const TOOL_LINKS = [
    { key: 'toolVideo' as const, href: path('/tools/video-converter') },
    { key: 'toolAudio' as const, href: path('/tools/audio-converter') },
    { key: 'toolImage' as const, href: path('/tools/image-compressor') },
    { key: 'toolZip' as const, href: path('/tools/zip') },
  ]

  const COMPANY_LINKS = [
    { key: 'footPrivacy' as const, href: path('/#privacy') },
    { key: 'footApi' as const, href: path('/#api') },
    { key: 'footPricing' as const, href: path('/#pricing') },
  ]

  return (
    <footer className="border-t bg-background">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-3">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <Cloud className="h-4 w-4" aria-hidden />
              </span>
              <span className="font-bold">CloudTools</span>
            </div>
            <p className="max-w-xs text-sm text-muted-foreground">{t('footTagline')}</p>
          </div>

          <div>
            <h3 className="text-sm font-semibold">{t('footTools')}</h3>
            <ul className="mt-3 space-y-2">
              {TOOL_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {t(link.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold">{t('footCompany')}</h3>
            <ul className="mt-3 space-y-2">
              {COMPANY_LINKS.map((link) => (
                <li key={link.key}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {t(link.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t pt-6">
          <p className="text-xs text-muted-foreground">
            {t('footRights', { year: new Date().getFullYear() })}
            {apiVersion && <span className="ml-2">· {t('footApiVersion', { version: apiVersion })}</span>}
          </p>
        </div>
      </div>
    </footer>
  )
}